import { useTelemetry } from '@iamexperiences/ecos-telemetry';
import { ScrollableContentWithFixedHeader } from '@iamexperiences/feature-app-launching';
import React, { lazy, ReactElement, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { CollectionModalsProvider } from '../atoms/CollectionModalsProvider';
import {
  PageHeaderProvider,
  useCurrentPageHeaders,
  usePageHeader,
} from '../atoms/PageHeaderProvider';
import { PageProvider, usePage } from '../atoms/PageProvider';
import { WarningMessageBar } from '../atoms/WarningMessageBar';
import { HeaderWrapper } from '../Header/HeaderWrapper';
import { LaunchPage } from './LaunchPage';
import { type ManageAppPageProps } from './ManageAppPage';
import { withTranslation } from 'react-i18next';
import { ReturnTimeSinceNavigationStarted } from '../../shared/Telemetry/PerformanceTelemetryHelper';
import {
  ILeftNavLocalizationMap,
  IPersonalInfo,
  SuiteLeftNav,
} from '@iamexperiences/feature-suite-left-nav';
import { useLocalization } from '../atoms/useLocalization';
import { useAuth } from '@iamexperiences/feature-auth';
import { useLeftNavData } from '../LeftNav/useLeftNavData';
import { mergeStyleSets } from '@fluentui/react';
import { PersonFeedback20Regular } from '@fluentui/react-icons';
import { useGetUserImage } from '../client-header-tenant/molecules/useGetUserImage';
import { Constants } from '../../shared/Constants/Constants';
import { useFeatureFlag } from '@iamexperiences/client-experiment';
import {
  useFeedback,
  FeedbackUnavailableDialog,
  FEEDBACK_CONTAINER_ID,
  FeedbackFeature
} from '../feedback';

export const ManageAppPage = lazy(
  async () => await import(/* webpackChunkName: "manage-app" */ './ManageAppPage')
);

export const SelfServiceApps = lazy(
  async () => await import('../feature-app-selfservice/SelfServiceApps')
);

declare global {
  interface Window {
    /**
     * Start time recorded during parsing of index.html.
     *
     * This can be used in the short term to determine loading times of the site.
     */

    MyAppsLoadStartMs: number;
  }
}

const myStyles = mergeStyleSets({
  text: {
    fontSize: '24px',
    fontWeight: 500,
  },
  headerWrapper: { position: 'fixed', top: 0, width: '100%', zIndex: 1000 },
  nav: { position: 'fixed', top: '48px', height: '100%', zIndex: 100 },
  mobileNav: { position: 'fixed', top: '48px', width: '100%', zIndex: 100 },
  mobileNavOpen: { position: 'fixed', top: '0', height: '100%', width: '100%', zIndex: 100 },
  pageContent: {
    position: 'absolute',
    left: '300px',
    width: 'calc(100% - 350px)',
  },
  mobilePageContent: {
    position: 'absolute',
    left: '24px',
    width: 'calc(100% - 40px)',
  },
  root: {
    // ...other styles for your root element
    ':global(#myAppsLeftNav .fui-NavDrawer)': {
      marginTop: '0px',
      // Add other overrides here
    },
  },
  desktopNav: {
    selectors: {
      '#myAppsLeftNav': {
        height: '100%',
      },
      '#myAppsLeftNav > div': {
        height: '100%',
      },
    },
},

});

const header = {
  id: 'suite-header',
  height: 48,
  behavior: 'pinned',
} as const;

const leftNav = {
  id: 'suite-left-nav',
  height: 48,
  behavior: 'pinned',
} as const;

interface LaunchPageConfig {
  readonly name: 'launch';
}

interface ManageAppPageConfig {
  readonly name: 'manageApp';
  readonly props: ManageAppPageProps;
}

interface SelfServiceAppsConfig {
  readonly name: 'requestNewApps';
}

type Page = LaunchPageConfig | ManageAppPageConfig | SelfServiceAppsConfig;

function PageContent(): ReactElement | null {
  const [page] = usePage<Page>();

  switch (page.name) {
    case 'launch':
      return <LaunchPage />;
    case 'manageApp':
      return <ManageAppPage {...page.props} />;
    case 'requestNewApps':
      return <SelfServiceApps />;
  }
}

function ScrollablePageWithHeaders(): ReactElement {
  const [page] = usePage<Page>();
  const telemetry = useTelemetry();
  const [t] = useLocalization();
  const { auth } = useAuth();
  const navigationItems = useLeftNavData();
  const { data: userImageUrl } = useGetUserImage(auth?.user?.objectId ?? '');
  const [isMobile, setIsMobile] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [showFeedbackUnavailable, setShowFeedbackUnavailable] = useState(false);

  const { value: feedbackEnabled = false } = useFeatureFlag('showFeedbackButton');
  // Feedback integration
  const { canDisplayFeedback, openFeedback } = useFeedback({
    feature: FeedbackFeature.MyApps,
    enabled: feedbackEnabled === true,
  });

  const handleFeedbackClick = useCallback(() => {
    if (!canDisplayFeedback) {
      setShowFeedbackUnavailable(true);
    } else {
      openFeedback();
    }
  }, [canDisplayFeedback, openFeedback]);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= Constants.MOBILE_BREAKPOINT_WIDTH);
  };

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkBackdrop = () => {
      setDrawerOpen(!!document.querySelector('.fui-OverlayDrawer__backdrop'));
    };
    checkBackdrop();
    const navEl = document.getElementById('myAppsLeftNav');
    let observer: MutationObserver | undefined;
    if (navEl) {
      observer = new MutationObserver(checkBackdrop);
      observer.observe(navEl, { childList: true, subtree: true });
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const time = ReturnTimeSinceNavigationStarted();
    telemetry.reportPerformance('Page Router', {
      data: { MyAppsLoadEnd: time },
    });
  }, [telemetry]);

  const localizationMap: ILeftNavLocalizationMap = useMemo(
    () => ({
      inProductFeedback: t('inProductFeedback'),
      openNavigationMenu: t('openNavigationMenu'),
      closeNavigationMenu: t('closeNavigationMenu'),
      profilePhoto: t('profilePhoto'),
      mainNavigation: t('mainNavigation'),
      menuItem: t('menuItem'),
    }),
    [t]
  );

  const person: IPersonalInfo = {
    userName: auth?.user?.name,
    userEmail: auth?.user?.email || auth?.user?.username,
    userPhotoUrl: userImageUrl,
    tenantId: auth?.user?.tenantId,
  };

  const suiteHeader = useMemo(() => ({ ...header, content: undefined }), []);
  usePageHeader(suiteHeader);

  const leftNavHeader = useMemo(() => ({ ...leftNav, content: undefined }), []);
  usePageHeader(leftNavHeader);

  const headers = useCurrentPageHeaders();

  const newHeaders = headers.map(header => ({
    ...header,
    dynamicWidth: true,
  }));

  return (
    <div className={isMobile ? '' : myStyles.desktopNav}>
      <div className={myStyles.headerWrapper}>
        <HeaderWrapper
          extraActions={feedbackEnabled
          ? [{ title: t('inProductFeedback'), icon: <PersonFeedback20Regular />, onClick: handleFeedbackClick }]
          : undefined} />
      </div>
      <div>
        <div
          className={
            isMobile ? (drawerOpen ? myStyles.mobileNavOpen : myStyles.mobileNav) : myStyles.nav
          }
        >
          <div id="myAppsLeftNav">
            <SuiteLeftNav
              personalInfo={person}
              navigationItems={navigationItems}
              localizationMap={localizationMap}
              selectedNavLink={'myApps'}
              onFeedbackClick={feedbackEnabled ? handleFeedbackClick : undefined}
            />
          </div>
        </div>
        <WarningMessageBar />
        <div role="main" className={isMobile ? myStyles.mobilePageContent : myStyles.pageContent}>
          <ScrollableContentWithFixedHeader
            overscroll={page.name === 'launch'}
            isContentFixedHeight={false}
            headers={newHeaders}
          >
            <Suspense fallback={<div></div>}>
              <PageContent />
            </Suspense>
          </ScrollableContentWithFixedHeader>
        </div>
      </div>
      <div id={FEEDBACK_CONTAINER_ID} />
      <FeedbackUnavailableDialog
        isOpen={showFeedbackUnavailable}
        onClose={() => setShowFeedbackUnavailable(false)}
      />
    </div>
  );
}

function PageRouterInner(): ReactElement {
  return (
    <PageProvider<Page> initialPage={{ name: 'launch' }}>
      <PageHeaderProvider>
        <CollectionModalsProvider>
          <ScrollablePageWithHeaders />
        </CollectionModalsProvider>
      </PageHeaderProvider>
    </PageProvider>
  );
}

export const PageRouter = withTranslation('Apps')(PageRouterInner);
